import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, CheckCircle2, ArrowRight, ArrowLeft, Clock, MapPin, Shield, Award, Users, ThumbsUp } from 'lucide-react';
import { companyInfo, collageImages, openingHours } from '../data';

export default function AboutUs() {
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll to top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update SEO Meta Tags
  useEffect(() => {
    document.title = "Om os | Peter Sloth Entreprenør";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    const descriptionText = "Læs mere om entreprenør Peter Sloth. Vi udfører professionelt jord-, beton- og belægningsarbejde i Horsens, Silkeborg, Skanderborg og Brædstrup med fokus på kompromisløs kvalitet.";
    if (metaDescription) {
      metaDescription.setAttribute("content", descriptionText);
    }
  }, []);

  // Form Submission Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Honeypot check
    if (data.website_url) {
      setIsSubmitting(false);
      setFormStatus({
        type: 'success',
        message: 'Tak for din henvendelse! Vi vender tilbage hurtigst muligt.',
      });
      e.target.reset();
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          service_requested: 'Generel forespørgsel (Om os side)',
          source_page: window.location.pathname,
        }),
      });

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: 'Tak for din henvendelse! Vi har modtaget din besked og vender tilbage hurtigst muligt.',
        });
        e.target.reset();
      } else {
        throw new Error('Fejl ved indsendelse');
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Der skete en fejl. Prøv venligst igen eller ring til os direkte på tlf. 51 81 51 75.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen pb-16">
      {/* Hero Banner Section */}
      <section className="relative w-full text-white overflow-hidden px-4 sm:px-6 lg:px-8 z-10">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0 scale-105"
          style={{ backgroundImage: `url('${collageImages.top}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/85 to-slate-900/60 z-0" />

        <div className="relative z-10 max-w-7xl mx-auto pt-44 pb-20 sm:pt-56 sm:pb-24 md:pt-64 md:pb-28 lg:pt-72 flex flex-col items-start text-left">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400 font-medium mb-6">
            <Link to="/" className="hover:text-sky-400 transition-colors">Forside</Link>
            <span>/</span>
            <span className="text-white font-bold">Om os</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight uppercase leading-none max-w-4xl">
            Om Peter Sloth
          </h1>
          <p className="mt-4 text-sm sm:text-base md:text-lg text-slate-300 max-w-2xl font-medium leading-relaxed">
            Vi går ikke på kompromis med kvaliteten. Læs historien bag vores entreprenørfirma og mød manden bag maskinerne.
          </p>
        </div>
      </section>

      {/* Main Content & Sidebar */}
      <section className="w-full px-4 sm:px-6 lg:px-8 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-start">
            
            {/* Left Column: Detailed Content */}
            <div className="lg:col-span-2 space-y-10 text-left">
            
            {/* Who We Are */}
            <div className="bg-white p-6 sm:p-10 border border-slate-200 shadow-xs">
              <h2 className="text-2xl font-extrabold text-slate-900 uppercase tracking-tight mb-6">
                Hvem er vi?
              </h2>
              <div className="space-y-6 text-slate-600 leading-relaxed text-base sm:text-lg">
                <p className="font-bold text-slate-800 border-l-4 border-sky-500 pl-4">
                  Hos entreprenørfirmaet Peter Sloth hjælper vi private og erhvervskunder i hele landet med professionelt jord-, beton- og belægningsarbejde. Vi har base i Brædstrup og dækker primært Horsens, Silkeborg, Skanderborg og omegn.
                </p>
                <p>
                  Firmaet er stiftet og drevet af Peter Sloth, som har en stor passion for entreprenørfaget. Vores målsætning har fra dag ét været at levere et stykke arbejde, som vi kan være stolte af, og som kunden kan have glæde af i mange årtier fremover. Vi ved, at fundamentet – både bogstaveligt og i overført betydning – skal være i orden, for at det efterfølgende byggeri bliver en succes.
                </p>
                <p>
                  Vi råder over en moderne og alsidig maskinpark, der gør os i stand til at løse alt fra mindre belægningsopgaver i private haver til store, komplekse udgravninger og nedbrydningsprojekter for erhvervskunder. Ingen opgave er for lille, og meget få er for store.
                </p>
              </div>
            </div>

            {/* Our Core Values */}
            <div className="bg-white p-6 sm:p-10 border border-slate-200 shadow-xs">
              <h2 className="text-2xl font-extrabold text-slate-900 uppercase tracking-tight mb-6">
                Vores Værdier
              </h2>
              <p className="text-slate-600 mb-8">
                Vores forretning bygger på sunde, jyske håndværksværdier. Når du samarbejder med Peter Sloth, kan du altid regne med følgende tre grundpiller:
              </p>

              <div className="space-y-8">
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <div className="text-black shrink-0 mt-1.5">
                    <Award className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Kvalitet uden kompromis</h3>
                    <p className="text-slate-600 text-sm mt-1 leading-relaxed">
                      Vi leverer kun arbejde af højeste standard. Vi tager os den tid, det kræver at lave et ordentligt stykke forarbejde – uanset om det er komprimering af sandpuden under et terrændæk eller afretning af bunden til en ny indkørsel. Det sikrer holdbare resultater uden efterfølgende sætningsskader.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <div className="text-black shrink-0 mt-1.5">
                    <Users className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Gennemskuelighed & Tillid</h3>
                    <p className="text-slate-600 text-sm mt-1 leading-relaxed">
                      Et godt samarbejde starter med tillid. Vi giver altid et klart og gennemskueligt tilbud uden skjulte ekstraregninger. Hvis der opstår uforudsete udfordringer undervejs (f.eks. i jorden under en udgravning), tager vi dialogen med dig med det samme, så vi i fællesskab finder den bedste og mest økonomiske løsning.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <div className="text-black shrink-0 mt-1.5">
                    <ThumbsUp className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Fleksibilitet & Punktlighed</h3>
                    <p className="text-slate-600 text-sm mt-1 leading-relaxed">
                      Vi overholder vores aftaler og tidsplaner. Vi arbejder fleksibelt og koordinerer gerne vores arbejde tæt med andre håndværkere på pladsen, så byggeprocessen glider så let og uforstyrret som muligt for dig som bygherre.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Our Process */}
            <div className="bg-white p-6 sm:p-10 border border-slate-200 shadow-xs">
              <h2 className="text-2xl font-extrabold text-slate-900 uppercase tracking-tight mb-8">
                Sådan arbejder vi
              </h2>
              <p className="text-slate-600 mb-8">
                Vi har gjort det nemt og trygt at være kunde hos os. Vores arbejdsproces sikrer, at du altid er fuldt informeret og tilfreds med resultatet:
              </p>

              <div className="relative border-l-2 border-slate-200 pl-6 ml-4 space-y-8 text-left">
                {/* Step 1 */}
                <div className="relative">
                  <div className="absolute -left-[35px] top-0 bg-orange-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
                    1
                  </div>
                  <h3 className="font-bold text-slate-900 text-base uppercase tracking-tight">Første kontakt & Rådgivning</h3>
                  <p className="text-slate-600 text-sm mt-1 leading-relaxed">
                    Du kontakter os med dit projekt. Vi tager en grundig snak om dine ønsker og behov. Ofte kommer vi ud og besigtiger opgaven på din adresse for at vurdere jordbundsforhold, adgangsveje og omfang.
                  </p>
                </div>

                {/* Step 2 */}
                <div className="relative">
                  <div className="absolute -left-[35px] top-0 bg-orange-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
                    2
                  </div>
                  <h3 className="font-bold text-slate-900 text-base uppercase tracking-tight">Uforpligtende tilbud</h3>
                  <p className="text-slate-600 text-sm mt-1 leading-relaxed">
                    Vi udarbejder et skriftligt og uforpligtende tilbud, hvor prisen og arbejdets omfang er klart beskrevet. Du ved præcis, hvad der er inkluderet, og hvad projektet vil koste.
                  </p>
                </div>

                {/* Step 3 */}
                <div className="relative">
                  <div className="absolute -left-[35px] top-0 bg-orange-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
                    3
                  </div>
                  <h3 className="font-bold text-slate-900 text-base uppercase tracking-tight">Udførelse med præcision</h3>
                  <p className="text-slate-600 text-sm mt-1 leading-relaxed">
                    Når aftalen er på plads, påbegynder vi arbejdet til den aftalte tid. Vi holder dig løbende opdateret om fremskridt og sørger for at rydde pænt op efter os selv undervejs og efter endt arbejde.
                  </p>
                </div>

                {/* Step 4 */}
                <div className="relative">
                  <div className="absolute -left-[35px] top-0 bg-orange-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
                    4
                  </div>
                  <h3 className="font-bold text-slate-900 text-base uppercase tracking-tight">Aflevering & Godkendelse</h3>
                  <p className="text-slate-600 text-sm mt-1 leading-relaxed">
                    Vi gennemgår det færdige resultat sammen med dig for at sikre, at alt lever op til dine forventninger og vores høje kvalitetskrav. Først når du er 100% tilfreds, betragter vi opgaven som fuldført.
                  </p>
                </div>
              </div>
            </div>

            {/* Collage Showcase */}
            <div className="bg-white p-6 sm:p-10 border border-slate-200 shadow-xs">
              <h2 className="text-2xl font-extrabold text-slate-900 uppercase tracking-tight mb-6">
                Vores arbejde i billeder
              </h2>
              <p className="text-slate-600 text-sm mb-6">
                Her er et par eksempler på projekter, vi har udført for glade kunder i lokalområdet:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="relative group overflow-hidden border border-slate-200 shadow-xs aspect-4/3 bg-slate-100">
                  <img
                    src={collageImages.bottomLeft}
                    alt="Støbning af sokkel og fundament"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    <p className="text-white text-xs font-bold uppercase tracking-wider">Støbning af sokkel og fundament</p>
                  </div>
                </div>
                <div className="relative group overflow-hidden border border-slate-200 shadow-xs aspect-4/3 bg-slate-100">
                  <img
                    src={collageImages.bottomRight}
                    alt="Flot belægningsarbejde"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    <p className="text-white text-xs font-bold uppercase tracking-wider">Kvalitetsbelægning i Brædstrup</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Back to Home Button */}
            <div className="flex justify-start">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-slate-900 hover:text-sky-500 font-bold uppercase text-xs tracking-wider transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Tilbage til forsiden
              </Link>
            </div>

          </div>

          {/* Right Column: Sidebar */}
          <div className="space-y-8 lg:sticky lg:top-28">
            
            {/* Quick Quote / Contact Form */}
            <div className="bg-slate-900 text-white p-6 sm:p-8 border border-slate-800 shadow-lg">
              <h3 className="text-lg font-extrabold uppercase tracking-wider text-white mb-2">
                Kontakt os i dag
              </h3>
              <p className="text-xs text-slate-400 mb-6">
                Udfyld formularen, så vender Peter Sloth personligt tilbage med et uforpligtende tilbud på dit projekt.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4 text-left">
                {/* Honeypot field - absolutely hidden from humans */}
                <div className="sr-only absolute" aria-hidden="true">
                  <label htmlFor="website_url">Hjemmeside URL</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Navn *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full bg-slate-950 border border-slate-800 text-white px-3 py-2 text-sm focus:outline-hidden focus:border-sky-500 transition-colors rounded-none"
                    placeholder="Dit fulde navn"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Telefon *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="w-full bg-slate-950 border border-slate-800 text-white px-3 py-2 text-sm focus:outline-hidden focus:border-sky-500 transition-colors rounded-none"
                      placeholder="Tlf. nr."
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">E-mail *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full bg-slate-950 border border-slate-800 text-white px-3 py-2 text-sm focus:outline-hidden focus:border-sky-500 transition-colors rounded-none"
                      placeholder="E-mail adresse"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Besked *</label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    className="w-full bg-slate-950 border border-slate-800 text-white px-3 py-2 text-sm focus:outline-hidden focus:border-sky-500 transition-colors rounded-none resize-none"
                    placeholder="Beskriv dit projekt eller stil et spørgsmål..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-slate-700 text-white font-bold uppercase text-xs tracking-wider py-3.5 px-4 transition-colors rounded-none cursor-pointer flex items-center justify-center gap-2"
                >
                  {isSubmitting ? 'Sender...' : 'Send besked'}
                  <ArrowRight className="w-4 h-4" />
                </button>

                {formStatus.message && (
                  <div className={`p-3 text-xs font-bold uppercase tracking-wider mt-4 ${
                    formStatus.type === 'success' ? 'bg-emerald-950/50 text-emerald-400 border border-emerald-800' : 'bg-rose-950/50 text-rose-400 border border-rose-800'
                  }`}>
                    {formStatus.message}
                  </div>
                )}
              </form>
            </div>

            {/* Quick Contact Info Card */}
            <div className="bg-white p-6 sm:p-8 border border-slate-200 shadow-xs text-left space-y-4">
              <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-900 pb-2 border-b border-slate-100">
                Direkte kontakt
              </h3>
              <p className="text-xs text-slate-600">
                Du er altid velkommen til at ringe eller skrive direkte til Peter Sloth.
              </p>
              <div className="space-y-3 text-xs font-bold text-slate-900">
                <a href={`tel:${companyInfo.phoneFormatted}`} className="flex items-center gap-3 hover:text-sky-500 transition-colors">
                  <Phone className="w-4 h-4 text-sky-500 shrink-0" />
                  <span>Ring: {companyInfo.phone}</span>
                </a>
                <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-3 hover:text-sky-500 transition-colors">
                  <Mail className="w-4 h-4 text-sky-500 shrink-0" />
                  <span className="truncate">Skriv: {companyInfo.email}</span>
                </a>
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-sky-500 shrink-0" />
                  <span>{companyInfo.address}</span>
                </div>
                <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                  <Shield className="w-4 h-4 text-sky-500 shrink-0" />
                  <span>CVR: {companyInfo.cvr}</span>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="bg-white p-6 sm:p-8 border border-slate-200 shadow-xs text-left">
              <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-900 mb-4 pb-2 border-b border-slate-100 flex items-center gap-2">
                <Clock className="w-4 h-4 text-sky-500" />
                <span>Åbningstider</span>
              </h3>
              <ul className="space-y-2 text-xs">
                {openingHours.map((item, index) => (
                  <li key={index} className="flex justify-between text-slate-600 font-medium">
                    <span className="font-bold text-slate-800">{item.day}</span>
                    <span>{item.hours}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>

        </div>
      </div>
      </section>
    </div>
  );
}
